import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './index.module.scss';
import HeadContainer from '../../../head-container';
import apiService from '../../../../utils/apiService';

const OTP_LENGTH = 6;
const MAX_RESEND_COUNT = 2;
const ATTEMPTS_EXHAUSTED_STATUS = 405;
const INVALID_CODE_CODE = 100006;
const EXPIRED_SESSION_CODE = 100005;
const INVALID_CODE_TEXT = 'Перевір коректність введеного коду';
const EXPIRED_SESSION_TEXT = 'Код більше не дійсний, надішли новий';
const COMMON_ERROR_TEXT = 'Щось пішло не так. Спробуй ще раз';

const OtpComponent = ({
  setScreenNumber,
  feedbackId,
  phoneNumber,
  handleChangePhoneNumber,
  successSendOtp,
  attemptsExhausted
}) => {
  const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(''));
  const [timeLeft, setTimeLeft] = useState(60);
  const [isResendAvailable, setIsResendAvailable] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const inputsRef = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }

    setIsResendAvailable(true);
  }, [timeLeft]);

  const handleResend = async () => {
    if (isResending || resendCount >= MAX_RESEND_COUNT) return;

    try {
      setIsResending(true);
      setError('');

      await apiService.get(`resend/${feedbackId}`);

      setResendCount(resendCount + 1);
      setOtp(new Array(OTP_LENGTH).fill(''));
      setTimeLeft(60);
      setIsResendAvailable(false);
    } catch (e) {
      if (e?.response?.status === ATTEMPTS_EXHAUSTED_STATUS) {
        attemptsExhausted();
        return;
      }

      setError(COMMON_ERROR_TEXT);
    } finally {
      setIsResending(false);
    }
  };

  const handleChange = (event, index) => {
    const value = event.target.value;

    if (value.length === OTP_LENGTH) {
      setOtp(value.split('').slice(0, OTP_LENGTH));
      return;
    }

    if (!isNaN(value) && value.length === 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (index < OTP_LENGTH - 1) inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Backspace') {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);

      if (index > 0) inputsRef.current[index - 1]?.focus();
    } else if (event.key >= '0' && event.key <= '9') {
      if (otp[index] !== '' && index < OTP_LENGTH - 1) {
        event.preventDefault();

        const newOtp = [...otp];
        newOtp[index + 1] = event.key;
        setOtp(newOtp);
        inputsRef.current[index + 1]?.focus();
      }
    }
  };

  const onSubmit = async () => {
    if (isLoading) return;

    const code = otp.join('');

    if (code.length !== OTP_LENGTH) {
      setError(INVALID_CODE_TEXT);
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      await apiService.post(`confirm/${feedbackId}`, {
        data: { code: parseInt(code, 10)?.toString() }
      });

      successSendOtp();
    } catch (e) {
      const status = e?.response?.status;
      const errorCode = Number(e?.response?.data?.code);

      if (status === ATTEMPTS_EXHAUSTED_STATUS) {
        attemptsExhausted();
        return;
      }

      if (status === 400 && errorCode === EXPIRED_SESSION_CODE) {
        setError(EXPIRED_SESSION_TEXT);
        setTimeLeft(0);
        setIsResendAvailable(true);
        return;
      }

      if (status === 400 && errorCode === INVALID_CODE_CODE) {
        setError(INVALID_CODE_TEXT);
        return;
      }

      setError(status === 400 ? INVALID_CODE_TEXT : COMMON_ERROR_TEXT);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!('OTPCredential' in window)) return;

    const abortController = new AbortController();

    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: abortController.signal })
      .then((otpCredential) => {
        if (otpCredential?.code) {
          setOtp(otpCredential.code.split('').slice(0, OTP_LENGTH));
        }
      })
      .catch(() => {});

    return () => abortController.abort();
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <HeadContainer setScreenNumber={setScreenNumber} />
        <div className={styles.content}>
          <p className={styles.title}>
            {`Ми надіслали повідомлення на номер ${
              phoneNumber || '380*********'
            }`}
          </p>
          <p className={styles.description}>
            Для підтвердження номеру введи код із SMS
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            <div className={styles.otpContainer}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => (inputsRef.current[index] = element)}
                  type="number"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                  onChange={(event) => handleChange(event, index)}
                  onFocus={() => setError('')}
                  autoComplete="one-time-code"
                  className={classNames(error && styles.errorInput)}
                />
              ))}
            </div>
          </form>
          {resendCount < MAX_RESEND_COUNT &&
            (isResendAvailable ? (
              <button
                className={styles.resendButton}
                onClick={handleResend}
                disabled={isResending}
              >
                Повторно отримати код
              </button>
            ) : (
              <p className={styles.timer}>
                Отримати код повторно через{' '}
                {String(Math.floor(timeLeft / 60)).padStart(2, '0')}:
                {String(timeLeft % 60).padStart(2, '0')}
              </p>
            ))}
          <div className={styles.buttonContainer}>
            {error && <p className={styles.error}>{error}</p>}
            <button
              className={classNames(
                styles.blueButton,
                isLoading && styles.blueButtonDisabled
              )}
              onClick={onSubmit}
              disabled={isLoading}
            >
              <span>Надіслати</span>
            </button>
            <button
              className={styles.whiteButton}
              onClick={handleChangePhoneNumber}
            >
              Змінити номер
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpComponent;
